import { useState } from 'react';
import type { UploadItem, UploadType } from '@vedjegy/shared';

const MAX_FILE_BYTES = 50 * 1024 * 1024; // 50 MB

interface FileUploaderProps {
  type: UploadType;
  label: string;
  accept: string;
  multiple: boolean;
  maxFiles?: number;
  items: UploadItem[];
  uploading: boolean;
  onFileSelect: (files: File[]) => void;
  onDelete: (id: string) => void;
  onDownload?: (id: string) => void;
}

/**
 * Dumb file upload slot for a single upload type.
 * Renders existing items, a delete button per item, and an add-file trigger.
 * All upload/delete logic lives in the parent.
 */
export default function FileUploader({
  label, accept, multiple, maxFiles, items, uploading, onFileSelect, onDelete, onDownload,
}: FileUploaderProps) {
  const [sizeError, setSizeError] = useState('');
  const atLimit = maxFiles != null && items.length >= maxFiles;

  return (
    <div style={{
      marginBottom: 20, padding: '16px 20px',
      border: '1px solid var(--border)', borderRadius: 'var(--radius-sm)',
    }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--primary)', marginBottom: 10 }}>{label}</div>

      {sizeError && (
        <div style={{ fontSize: 12, color: '#c0392b', marginBottom: 8 }}>{sizeError}</div>
      )}

      {items.map((u) => (
        <div key={u.id} style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '6px 10px', background: 'var(--muted)',
          borderRadius: 'var(--radius-sm)', marginBottom: 6, fontSize: 13,
        }}>
          {onDownload ? (
            <button
              className="btn btn-ghost btn-sm"
              style={{ padding: 0, fontSize: 13, fontWeight: 400, color: 'var(--primary)', textDecoration: 'underline', cursor: 'pointer', background: 'none', border: 'none' }}
              onClick={() => onDownload(u.id)}
            >
              {u.fileName}
            </button>
          ) : (
            <span>{u.fileName}</span>
          )}
          {u.fileSize != null && (
            <span style={{ fontSize: 11, color: 'var(--muted-foreground)', marginLeft: 8 }}>
              {(u.fileSize / 1024).toFixed(0)} KB
            </span>
          )}
          <button
            className="btn btn-ghost btn-sm"
            style={{ padding: '2px 8px', fontSize: 12, marginLeft: 'auto' }}
            onClick={() => onDelete(u.id)}
          >
            x
          </button>
        </div>
      ))}

      {maxFiles != null && (
        <div style={{ fontSize: 11, color: 'var(--muted-foreground)', marginBottom: 6 }}>
          {items.length} / {maxFiles} fájl feltöltve
        </div>
      )}

      {(multiple || items.length === 0) && !atLimit && (
        <label style={{ display: 'inline-block', cursor: uploading ? 'wait' : 'pointer' }}>
          <input
            type="file"
            accept={accept}
            multiple={multiple}
            style={{ display: 'none' }}
            onChange={(e) => {
              const files = Array.from(e.target.files ?? []);
              const oversized = files.filter((f) => f.size > MAX_FILE_BYTES);
              if (oversized.length > 0) {
                setSizeError('A feltöltendő fájlok között túlméretezett fájl van (max 50 MB): ' + oversized.map((f) => f.name).join(', '));
                e.target.value = '';
                return;
              }
              if (maxFiles != null && items.length + files.length > maxFiles) {
                setSizeError(`Legfeljebb ${maxFiles} fájl tölthető fel.`);
                e.target.value = '';
                return;
              }
              setSizeError('');
              if (files.length) onFileSelect(files);
              e.target.value = '';
            }}
          />
          <span className="btn btn-ghost btn-sm" style={{ fontSize: 12 }}>
            {uploading ? 'Feltöltés...' : '+ Fájl hozzáadása'}
          </span>
        </label>
      )}

      {atLimit && (
        <div style={{ fontSize: 12, color: 'var(--muted-foreground)', fontStyle: 'italic' }}>
          Elérte a maximális fájlszámot ({maxFiles} db). Töröljön egyet a cseréhez.
        </div>
      )}
    </div>
  );
}
